import { proxyAxios } from 'api/apiBase/axios';
import { normalizeNumber } from 'utils/formats/normalizeNumber';
import { ECexName } from 'web3';

type TickAndPrice = { tick: number; price: number };

const get = async <T>(url: string) => {
  const { data } = await proxyAxios.get<T>('/api/v1/proxy', { params: { url } });
  return data as T;
};

const d = (x: unknown) => Number(x);

const pow10 = (n: number) => Math.pow(10, -n);

// +
async function binance(base: string, quote: string): Promise<TickAndPrice> {
  const sym = `${base}${quote}`;
  const exinfo = await get<{
    symbols: {
      symbol: string;
      filters: { filterType: string; tickSize?: string }[];
      pricePrecision?: number;
    }[];
  }>(`https://api.binance.com/api/v3/exchangeInfo?symbol=${sym}`);

  const s = exinfo.symbols?.[0];
  let tick = 0;
  if (s) {
    const pf = s.filters.find(f => f.filterType === 'PRICE_FILTER');
    if (pf?.tickSize) tick = d(pf.tickSize);
    else if (typeof s.pricePrecision === 'number') tick = pow10(s.pricePrecision);
  }
  const t = await get<{ price: string }>(
    `https://api.binance.com/api/v3/ticker/price?symbol=${sym}`,
  );
  return { tick, price: d(t.price) };
}

// +
async function okx(base: string, quote: string): Promise<TickAndPrice> {
  const inst = `${base}-${quote}`;
  const i = await get<{ data?: { tickSz: string }[] }>(
    `https://www.okx.com/api/v5/public/instruments?instType=SPOT&instId=${inst}`,
  );
  const tick = i.data && i.data[0]?.tickSz ? d(i.data[0].tickSz) : 0;
  const t = await get<{ data?: { last: string }[] }>(
    `https://www.okx.com/api/v5/market/ticker?instId=${inst}`,
  );
  const price = t.data && t.data[0]?.last ? d(t.data[0].last) : 0;
  return { tick, price };
}

// +
async function kucoin(base: string, quote: string): Promise<TickAndPrice> {
  const inst = `${base}-${quote}`;
  const info = await get<{ data?: { priceIncrement: string } }>(
    `https://api.kucoin.com/api/v2/symbols/${inst}`,
  );
  const pincr = info.data?.priceIncrement ?? '0';
  const tick = d(pincr);
  const tj = await get<{ data: { price: string } }>(
    `https://api.kucoin.com/api/v1/market/orderbook/level1?symbol=${inst}`,
  );
  return { tick, price: d(tj.data.price) };
}

// + (but better to clarify the API's with the documentation) - where I can see the current result: http://localhost:3001/project/78/cex-pair/442
async function kraken(base: string, quote: string): Promise<TickAndPrice> {
  const quoteMap: Record<string, string> = { BTC: 'XBT' };
  const q = quoteMap[quote] ?? quote;
  const pair = `${base}${q}`;
  const info = await get<{ result: Record<string, any> }>(
    `https://api.kraken.com/0/public/AssetPairs?pair=${pair}`,
  );
  const entry = Object.values(info.result ?? {})[0] as any;
  let tick = 0;
  if (entry) {
    if (entry.tick_size) tick = d(entry.tick_size);
    else if (typeof entry.pair_decimals === 'number') tick = pow10(entry.pair_decimals);
  }
  const t = await get<{ result: Record<string, { c: [string] }> }>(
    `https://api.kraken.com/0/public/Ticker?pair=${Object.keys(info.result ?? {})[0]}`,
  );
  const price = d(Object.values(t.result ?? {})[0]?.c?.[0] ?? 0);
  return { tick, price };
}

// +
async function bitget(base: string, quote: string): Promise<TickAndPrice> {
  const inst = `${base}${quote}`;
  const pj = await get<{ data?: { category: string; symbol: string; pricePrecision: number }[] }>(
    `https://api.bitget.com/api/v3/market/instruments?category=SPOT`,
  );
  const rec = (pj.data ?? []).find(
    (x: any) => x.category === 'SPOT' || x.symbol === inst || String(x.symbol).startsWith(inst),
  );

  const tick = pow10(rec?.pricePrecision ?? 0);
  const sym = rec?.symbol ?? inst;
  const tj = await get<{ data?: any[] }>(
    `https://api.bitget.com/api/v3/market/tickers?category=SPOT&symbol=${sym}`,
  );
  const row = (tj.data ?? []).find((x: any) => x.symbol === sym) ?? (tj.data ?? [])[0];
  const price = d(row?.lastPrice ?? 0);
  return { tick, price };
}

// +
async function mexc(base: string, quote: string): Promise<TickAndPrice> {
  const inst = `${base}${quote}`;
  const ex = await get<{ symbols?: any[] }>(
    `https://api.mexc.com/api/v3/exchangeInfo?symbol=${inst}`,
  );
  const info = ex.symbols?.[0];
  let tick = 0;
  if (info) {
    const pf = (info.filters ?? []).find((f: any) => f.filterType === 'PRICE_FILTER');
    if (pf?.tickSize) tick = d(pf.tickSize);
    else if (typeof info.pricePrecision === 'number') tick = pow10(info.pricePrecision);
    else if (typeof info.quotePrecision === 'number') tick = pow10(info.quotePrecision);
  }
  const t = await get<{ price: string }>(`https://api.mexc.com/api/v3/ticker/price?symbol=${inst}`);
  return { tick, price: d(t.price) };
}

// +
async function gate(base: string, quote: string): Promise<TickAndPrice> {
  const inst = `${base}_${quote}`;
  const info = await get<{ precision?: number; price_precision?: number }>(
    `https://api.gateio.ws/api/v4/spot/currency_pairs/${inst}`,
  );
  const precision = (info as any).precision ?? (info as any).price_precision ?? 0;
  const tick = typeof precision === 'number' ? pow10(precision) : 0;
  const t = await get<any[]>(`https://api.gateio.ws/api/v4/spot/tickers?currency_pair=${inst}`);
  const price = d(t?.[0]?.last ?? 0);
  return { tick, price };
}

// +
async function bingx(base: string, quote: string): Promise<TickAndPrice> {
  const symbol = `${base}-${quote}`;
  const j = await get<{ data?: { symbols?: any[] } }>(
    `https://open-api.bingx.com/openApi/spot/v1/common/symbols`,
  );
  const rec = (j.data?.symbols ?? []).find((x: any) => x.symbol === symbol);
  const tickSize = rec?.tickSize ?? '0.01';
  const tick = d(tickSize);
  const t = await get<{ data?: any[] }>(
    `https://open-api.bingx.com/openApi/spot/v1/ticker/bookTicker?symbol=${symbol}`,
  );
  const row = (t.data ?? [])[0];
  const bid = d(row?.bidPrice ?? 0);
  const ask = d(row?.askPrice ?? 0);
  const price = bid && ask ? (bid + ask) / 2 : bid || ask || 0;
  return { tick, price };
}

// +
async function whitebit(base: string, quote: string): Promise<TickAndPrice> {
  const inst = `${base}_${quote}`;
  const markets = await get<any[]>(`https://whitebit.com/api/v4/public/markets`);
  const market = (markets ?? []).find((m: any) => m.name === inst);
  const moneyPrec = market?.moneyPrec ?? 0;
  const tick = pow10(Number(moneyPrec));
  const ticker = await get<Record<string, any>>(`https://whitebit.com/api/v4/public/ticker`);
  const price = d(ticker?.[inst]?.last_price ?? ticker?.[inst]?.last ?? 0);
  return { tick, price };
}

// +
async function bybit(base: string, quote: string): Promise<TickAndPrice> {
  const symbol = `${base}${quote}`;
  const instr = await get<{ result?: { list?: any[] }; retCode: number; retMsg: string }>(
    `https://api.bybit.com/v5/market/instruments-info?category=spot&symbol=${symbol}`,
  );
  const row = instr.result?.list?.[0];
  const tick = row?.priceFilter?.tickSize ? d(row.priceFilter.tickSize) : 0;
  const t = await get<{ result?: { list?: any[] }; retCode: number; retMsg: string }>(
    `https://api.bybit.com/v5/market/tickers?category=spot&symbol=${symbol}`,
  );
  const price = d(t.result?.list?.[0]?.lastPrice ?? 0);
  return { tick, price };
}

// +
async function htx(base: string, quote: string): Promise<TickAndPrice> {
  const inst = `${base}${quote}`.toLowerCase();
  const j = await get<{ data?: any[] }>(`https://api.huobi.pro/v1/common/symbols`);
  const rec = (j.data ?? []).find((x: any) => x.symbol === inst);
  const pricePrecision = rec?.['price-precision'];
  const tick = typeof pricePrecision === 'number' ? pow10(pricePrecision) : 0;
  const t = await get<{ tick?: { close?: string } }>(
    `https://api.huobi.pro/market/detail/merged?symbol=${inst}`,
  );
  const price = d(t.tick?.close ?? 0);
  return { tick, price };
}

// +
async function bitmart(base: string, quote: string): Promise<TickAndPrice> {
  const inst = `${base}_${quote}`;
  const t = await get<{
    data?: {
      symbol: string;
      last?: string;
      bid_px?: string;
      ask_px?: string;
    };
  }>(`https://api-cloud.bitmart.com/spot/quotation/v3/ticker?symbol=${inst}`);

  const last = t.data?.last ?? '0';
  const bid = d(t.data?.bid_px ?? '0');
  const ask = d(t.data?.ask_px ?? '0');
  const price = d(last);

  // Derive tick from bid/ask spread (API metadata is unreliable for BitMart)
  // The spread represents the minimum price increment when best bid/ask are 1 tick apart
  let tick = 0;
  if (bid > 0 && ask > 0 && ask > bid) {
    const spread = ask - bid;
    // Normalize to avoid float artifacts (e.g., 0.00009999... → 0.0001)
    const normalized = Number(spread.toFixed(12).replace(/0+$/, '').replace(/\.$/, ''));
    tick = normalized > 0 ? normalized : spread;
  }

  return { tick, price };
}

// +
async function xt(base: string, quote: string): Promise<TickAndPrice> {
  const inst = `${base.toLowerCase()}_${quote.toLowerCase()}`;
  const j = await get<{ result?: { symbols?: { pricePrecision?: number }[] } }>(
    `https://sapi.xt.com/v4/public/symbol?symbol=${inst}`,
  );
  const precision = j.result?.symbols?.[0]?.pricePrecision ?? 0;
  const tick = pow10(precision);
  const t = await get<{ result?: { p?: string }[] }>(
    `https://sapi.xt.com/v4/public/ticker/price?symbol=${inst}`,
  );
  const price = d((t.result as any)?.[0]?.p ?? 0);
  return { tick, price };
}

// +
async function lbank(base: string, quote: string): Promise<TickAndPrice> {
  const inst = `${base.toLowerCase()}_${quote.toLowerCase()}`;
  const j = await get<{ data?: { price?: string }[] }>(
    `https://api.lbkex.com/v2/supplement/ticker/price.do?symbol=${inst}`,
  );
  const priceStr = j.data?.[0]?.price ?? '0';
  const price = d(priceStr);
  const decimals = (priceStr.split('.')[1] ?? '').length;
  const tick = pow10(decimals || 2);
  return { tick, price };
}

// - (error fetching symbol (info)) - http://localhost:3001/project/106/cex-pair/153
async function coinstore(base: string, quote: string): Promise<TickAndPrice> {
  const sym = `${base.toUpperCase()}${quote.toUpperCase()}`;
  // Public endpoint with all tickers; filter client-side for our symbol
  const tickers = await get<{
    data?: {
      symbol?: string;
      close?: string | number;
      bid?: string | number;
      ask?: string | number;
    }[];
  }>(`https://api.coinstore.com/api/v1/market/tickers`);

  const row = (tickers.data ?? []).find(r => r.symbol === sym);
  const closeVal = row?.close ?? 0;
  const bidVal = row?.bid ?? 0;
  const askVal = row?.ask ?? 0;

  let price = d(closeVal);
  if (!price) {
    const bid = d(bidVal);
    const ask = d(askVal);
    price = bid && ask ? (bid + ask) / 2 : bid || ask || 0;
  }

  const decLen = (v: unknown) => {
    const s = String(v ?? '');
    const idx = s.indexOf('.');
    return idx === -1 ? 0 : s.length - idx - 1;
  };
  const decimals = Math.max(decLen(closeVal), decLen(bidVal), decLen(askVal));
  const tick = pow10(decimals || 0);
  return { tick, price };
}

// +
async function indodax(base: string, quote: string): Promise<TickAndPrice> {
  const sym = `${base.toLowerCase()}${quote.toLowerCase()}`;
  // Ticker
  const t = await get<{ ticker?: { last?: string } }>(`https://indodax.com/api/ticker/${sym}`);
  const priceStr = (t as any)?.ticker?.last ?? '0';
  const price = d(priceStr);
  // Precision heuristic (aligns with strategy stub; adjust if better endpoint available)
  let precision = 1;
  if (base.toUpperCase() === 'ISLM') precision = 5;
  const tick = pow10(precision);
  return { tick, price };
}

// +
async function hotcoin(base: string, quote: string): Promise<TickAndPrice> {
  const inst = `${base.toLowerCase()}_${quote.toLowerCase()}`;
  const depthUrl = `https://api.hotcoinfin.com/v1/depth?symbol=${inst}&type=step0`;
  const depth = await get<{
    code: number;
    data?: { depth?: { bids?: [string, string][]; asks?: [string, string][]; lastPrice?: number } };
  }>(depthUrl);

  const bids = depth.data?.depth?.bids ?? [];
  const asks = depth.data?.depth?.asks ?? [];
  const priceLevels: number[] = [...bids, ...asks]
    .slice(0, 200)
    .map(row => d(row?.[0]))
    .filter(v => Number.isFinite(v));

  let tick = 0;
  if (priceLevels.length >= 2) {
    const uniq = Array.from(new Set(priceLevels)).sort((a, b) => a - b);
    let minDiff = Number.POSITIVE_INFINITY;
    for (let i = 1; i < uniq.length; i += 1) {
      const diff = Math.abs(uniq[i] - uniq[i - 1]);
      if (diff > 0 && diff < minDiff) minDiff = diff;
    }
    if (minDiff !== Number.POSITIVE_INFINITY) {
      // Normalize to avoid float artifacts, e.g., 0.010000000000000009
      const normalized = Number(String(minDiff.toFixed(12)).replace(/0+$/, '').replace(/\.$/, ''));
      tick = normalized > 0 ? normalized : minDiff;
    }
  }

  let price = d(depth.data?.depth?.lastPrice ?? 0);
  if (!price) {
    // Fallback: use kline close as last price
    const klineUrl = `https://api.hotcoinfin.com/v1/ticker?symbol=${inst}&step=60`;
    const t = await get<{ code: number; data?: any }>(klineUrl);
    const rows = (t as any)?.data as any[] | undefined;
    const lastRow = Array.isArray(rows) && rows.length > 0 ? rows[rows.length - 1] : null;
    const closeStr = lastRow?.[4] ?? 0;
    price = d(closeStr);
  }

  if (!tick && price) {
    // Final fallback: infer tick from price decimals
    const decimals = String(price).split('.')[1]?.length ?? 0;
    if (decimals > 0) tick = pow10(decimals);
  }

  return { tick, price };
}

export async function getTickPercent(params: {
  cex: ECexName;
  base: string;
  quote: string;
}): Promise<number | null> {
  console.log('getTickPercent', params);
  const { cex, base, quote } = params;
  let res: TickAndPrice | null = null;
  try {
    switch (cex) {
      case ECexName.binance:
        res = await binance(base, quote);
        break;
      case ECexName.okx:
        res = await okx(base, quote);
        break;
      case ECexName.kucoin:
        res = await kucoin(base, quote);
        break;
      case ECexName.kraken:
        res = await kraken(base, quote);
        break;
      case ECexName.bitget:
        res = await bitget(base, quote);
        break;
      case ECexName.mexc:
        res = await mexc(base, quote);
        break;
      case ECexName.gate:
        res = await gate(base, quote);
        break;
      case ECexName.bingx:
        res = await bingx(base, quote);
        break;
      case ECexName.whitebit:
        res = await whitebit(base, quote);
        break;
      case ECexName.bybit:
        res = await bybit(base, quote);
        break;
      case ECexName.htx:
        res = await htx(base, quote);
        break;
      case ECexName.bitmart:
        res = await bitmart(base, quote);
        break;
      case ECexName.xt:
        res = await xt(base, quote);
        break;
      case ECexName.lbank:
        res = await lbank(base, quote);
        break;
      case ECexName.coinstore:
        res = await coinstore(base, quote);
        break;
      case ECexName.indodax:
        res = await indodax(base, quote);
        break;
      case ECexName.hotcoin:
        res = await hotcoin(base, quote);
        break;
      default:
        res = null;
    }
  } catch (e) {
    res = null;
  }

  if (!res || !res.price || !res.tick) return null;
  // pct = (tick / price) * 100
  const pct = (res.tick / res.price) * 100;
  // Normalize to a reasonable number of decimals
  return d(normalizeNumber(pct));
}
